import { getMediaJourney } from "constants/mediaMapping";

const MEDIA_HOARDING_DIGITAL_SUBCATEGORY_IDS = new Set([
  "643cdf01779bc024c189cf95",
  "65029534eaa5251874e8c6b6",
  "650296faeaa5251874e8c716",
]);

const normalizeValue = (value) =>
  typeof value === "string" ? value.trim() : value;
const normalizeJourney = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const normalizeListingType = (value) => {
  const raw = normalizeValue(value);
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (lower === "media") return "Media";
  if (lower === "voucher") return "Voucher";
  if (lower === "product") return "Product";
  return raw;
};

const normalizeSubCategoryId = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "object" && value._id != null) {
    return String(value._id);
  }
  return String(value).trim();
};

const normalizeSubCategoryName = (product = {}) =>
  normalizeValue(
    product.ProductSubCategoryName ||
      product.productSubCategoryName ||
      product.subCategoryName ||
      product.ProductType ||
      product.productType ||
      "",
  );

const normalizeCategoryName = (product = {}) =>
  normalizeValue(
    product.ProductCategoryName ||
      product.productCategoryName ||
      product.category ||
      product.categoryName ||
      "",
  );

const DIGITAL_AND_HOARDING_JOURNEYS = new Set(["digital-ads", "hoarding"]);
const MULTIPLEX_BUYING_JOURNEYS = new Set([
  "multiplex",
  "display-video",
  "newspaper",
  "television-ads",
  "airport",
  "btl",
]);
const ALL_KNOWN_MEDIA_JOURNEYS = new Set([
  ...DIGITAL_AND_HOARDING_JOURNEYS,
  ...MULTIPLEX_BUYING_JOURNEYS,
]);

const isVoucherProduct = (product) => {
  const listingType = normalizeValue(
    product?.ListingType || product?.listingType,
  );
  return (
    listingType === "Voucher" ||
    product?.ProductSubCategory === "Voucher" ||
    product?.ProductSubCategoryName === "Voucher"
  );
};

const hasJourneySignal = (product) => {
  if (!product || typeof product !== "object") return false;
  return (
    Object.prototype.hasOwnProperty.call(product, "mediaJourney") ||
    Object.prototype.hasOwnProperty.call(product, "mediaCategory")
  );
};

const resolveMediaJourney = (product) => {
  const explicitJourney = normalizeJourney(product?.mediaJourney);
  if (explicitJourney) return explicitJourney;
  const mappedJourney = normalizeJourney(
    getMediaJourney(product?.mediaCategory),
  );
  if (mappedJourney) return mappedJourney;

  // Some APIs already return journey slug directly in mediaCategory.
  const directCategoryJourney = normalizeJourney(product?.mediaCategory);
  if (ALL_KNOWN_MEDIA_JOURNEYS.has(directCategoryJourney)) {
    return directCategoryJourney;
  }

  return "";
};

const isMultiplexMedia = (product) => {
  const journey = resolveMediaJourney(product);
  return (
    MULTIPLEX_BUYING_JOURNEYS.has(journey) ||
    normalizeValue(product?.ProductCategoryName) === "Multiplex ADs"
  );
};

const isDigitalOrHoardingMedia = (product) => {
  const subCategoryName = normalizeSubCategoryName(product);
  const categoryName = normalizeCategoryName(product);
  const subCategoryLower = subCategoryName.toLowerCase();
  const categoryLower = categoryName.toLowerCase();
  const journey = resolveMediaJourney(product);
  const subCategoryId = normalizeSubCategoryId(
    product?.ProductSubCategory ?? product?.productSubCategory ?? product?.subCategoryId,
  );

  return (
    DIGITAL_AND_HOARDING_JOURNEYS.has(journey) ||
    MEDIA_HOARDING_DIGITAL_SUBCATEGORY_IDS.has(subCategoryId) ||
    subCategoryLower === "hoardings" ||
    categoryLower === "hoarding" ||
    categoryLower === "hoardings" ||
    subCategoryLower === "digital ads" ||
    subCategoryLower === "led ooh" ||
    subCategoryLower === "dooh" ||
    categoryLower === "dooh"
  );
};

const isMediaProduct = (product) => {
  const listingType = normalizeListingType(
    product?.ListingType || product?.listingType,
  );
  const journey = resolveMediaJourney(product);
  if (listingType === "Media" || journey !== "") return true;
  if (product?.ProductSubCategory === "Media") return true;
  if (product?.ProductSubCategoryName === "Media") return true;
  const categoryName = normalizeValue(product?.ProductCategoryName);
  if (categoryName === "MediaOnline" || categoryName === "MediaOffline")
    return true;
  return false;
};

export const isMediaMissingJourney = (product) => {
  if (!isMediaProduct(product)) return false;
  if (!hasJourneySignal(product)) return false;
  return resolveMediaJourney(product) === "";
};

export const getProductListingTypeForState = (product) => {
  if (isVoucherProduct(product)) return "Voucher";
  if (isMediaProduct(product)) return "Media";
  return "Product";
};

const listingTypeKey = (product) =>
  normalizeListingType(product?.ListingType || product?.listingType).toLowerCase();

export const buildProductRoutingPayload = (product = {}) => {
  if (!product || typeof product !== "object") return product;

  const subCategoryName = normalizeSubCategoryName(product);
  const categoryName = normalizeCategoryName(product);
  const subCategoryId = normalizeSubCategoryId(
    product.ProductSubCategory ??
      product.productSubCategory ??
      product.subCategoryId,
  );
  const subCategoryLower = subCategoryName.toLowerCase();
  const categoryLower = categoryName.toLowerCase();

  let mediaCategory = normalizeJourney(
    product.mediaCategory ?? product.MediaCategory ?? "",
  );
  let mediaJourney = normalizeJourney(
    product.mediaJourney ?? product.MediaJourney ?? "",
  );

  if (!mediaCategory) {
    const productCategorySlug = normalizeJourney(
      product.productCategory ?? product.ProductCategory ?? "",
    );
    if (
      productCategorySlug === "hoarding" ||
      productCategorySlug === "dooh" ||
      productCategorySlug === "multiplex"
    ) {
      mediaCategory = productCategorySlug;
    } else if (
      subCategoryLower === "hoardings" ||
      categoryLower === "hoarding" ||
      categoryLower === "hoardings" ||
      MEDIA_HOARDING_DIGITAL_SUBCATEGORY_IDS.has(subCategoryId)
    ) {
      mediaCategory = "hoarding";
    } else if (
      subCategoryLower === "digital ads" ||
      subCategoryLower === "led ooh" ||
      subCategoryLower === "dooh" ||
      categoryLower === "dooh"
    ) {
      mediaCategory = "dooh";
    } else if (
      subCategoryLower === "multiplex ads" ||
      categoryLower === "multiplex ads"
    ) {
      mediaCategory = "multiplex";
    }
  }

  if (!mediaJourney && mediaCategory) {
    mediaJourney =
      normalizeJourney(getMediaJourney(mediaCategory)) || mediaCategory;
  }

  const listingType = normalizeListingType(
    product.ListingType || product.listingType || "",
  );

  return {
    ...product,
    _id: product._id || product.productId || product.id,
    ListingType: listingType,
    listingType: listingType || product.listingType,
    ProductSubCategory: subCategoryId || product.ProductSubCategory,
    ProductSubCategoryName: subCategoryName || product.ProductSubCategoryName,
    ProductCategoryName: categoryName || product.ProductCategoryName,
    mediaCategory,
    mediaJourney,
  };
};

/**
 * Canonical detail URL for marketplace / cart / wishlist / notifications.
 * Uses listing type, mediaCategory, order-request snapshots (isMultiplexMedia, etc.),
 * then category / journey fallbacks so the route always matches buyer media flows.
 */
export const getProductDetailPath = (product) => {
  const routingProduct = buildProductRoutingPayload(product);
  const id = routingProduct?._id || routingProduct?.id;
  if (!id) return "/home/productdetail";

  const lt = listingTypeKey(routingProduct);

  if (isVoucherProduct(routingProduct) || lt === "voucher") {
    return `/home/voucherdetail/${id}`;
  }

  const treatAsMedia =
    lt === "media" ||
    routingProduct?.isMediaLine === true ||
    isMediaProduct(routingProduct);

  if (treatAsMedia) {
    // Order Request / cart line snapshots (buy again, wallet, etc.)
    if (routingProduct?.isMultiplexMedia) return `/home/multiplexmediabuying/${id}`;
    if (routingProduct?.isHoardingMedia || routingProduct?.isDigitalAdsMedia) {
      return `/home/digitalAndHoardingMediaBuying/${id}`;
    }

    const mc = normalizeJourney(routingProduct?.mediaCategory);

    if (mc === "television") return `/home/mediabuying/${id}`;
    if (mc === "multiplex") return `/home/multiplexmediabuying/${id}`;
    if (mc === "hoarding" || mc === "dooh")
      return `/home/digitalAndHoardingMediaBuying/${id}`;
    if (
      mc === "radio" ||
      mc === "print" ||
      mc === "other" ||
      mc === "offlinebtl" ||
      mc === "airport"
    ) {
      return `/home/mediabuying/${id}`;
    }
    if (isMultiplexMedia(routingProduct)) {
      return `/home/multiplexmediabuying/${id}`;
    }
    if (isDigitalOrHoardingMedia(routingProduct)) {
      return `/home/digitalAndHoardingMediaBuying/${id}`;
    }
    return `/home/mediabuying/${id}`;
  }

  if (lt === "product" || lt === "") {
    return `/home/productdetail/${id}`;
  }

  return `/home/productdetail/${id}`;
};

/**
 * Navigate to the canonical product / media / voucher detail route.
 * @param {import('react-router-dom').NavigateFunction} navigate
 * @param {object} product – same shape as for {@link getProductDetailPath}
 * @param {import('react-router-dom').NavigateOptions} [navigateOptions] – e.g. `{ state: { type: 'Media' } }`
 */
export const navigateToProductDetail = (navigate, product, navigateOptions) => {
  if (typeof navigate !== "function") return;
  const path = getProductDetailPath(product);
  if (!path) return;
  navigate(path, navigateOptions);
};

export const MEDIA_ROUTE_HELPERS = {
  MEDIA_HOARDING_DIGITAL_SUBCATEGORY_IDS,
  buildProductRoutingPayload,
  isVoucherProduct,
  isMultiplexMedia,
  isDigitalOrHoardingMedia,
  isMediaProduct,
  isMediaMissingJourney,
  resolveMediaJourney,
};
